// components/CustomSelect.jsx
import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import axios from 'axios';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';

const CustomSelect = ({
  name,
  value,
  onChange,
  options: staticOptions,
  endpoint,
  isMulti = false,
  placeholder = 'Select...',
  isClearable = true,
  disabled = false,
  loadOnMount = true,
  fetchParams = {},        // additional query params
  cacheOptions = true,
  valueField = 'value',
  labelField = 'label',
  isSearchable = false,
  formatOptionLabel,
}) => {
  const [options, setOptions] = useState(staticOptions || []);
  const [loading, setLoading] = useState(false);

  // Fetch options from endpoint
  const fetchOptions = useCallback(
    debounce(async (input) => {
      if (!endpoint) return;
      setLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_SERVER_BASE_URL}${endpoint}`, {
          params: { query: input, ...fetchParams },
        });
        // Transform response data if necessary
        const transformedOptions = response.data.map((item) => ({
          value: item[valueField],
          label: item[labelField],
        }));
        // Set options state  
        setOptions(transformedOptions);

      } catch (err) {
        console.error('CustomSelect fetch error:', err);
      } finally {
        setLoading(false);
      }
    }, 300),
    [endpoint, fetchParams]
  );

  // initial load if desired
  useEffect(() => {
    if (endpoint && loadOnMount) {
      fetchOptions('');
    }
  }, [endpoint, loadOnMount]);

  // If staticOptions prop changes, override
  useEffect(() => {
    if (Array.isArray(staticOptions)) {
      setOptions(staticOptions);
    }
  }, [staticOptions]);

  const handleInputChange = (newValue) => {
    if (endpoint) {
      fetchOptions(newValue);
    }
    return newValue;
  };

  const handleChange = (selected, action) => {
    // Pass both value and full selected option to parent
    onChange(name, selected?.value, selected);
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      height: '40px',
      minHeight: '40px',
      backgroundColor: state.isDisabled ? '#e9ecef' : provided.backgroundColor,
      padding: '0px',
      marginBottom: '0px',
      marginRight: '0px',
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: '40px',
      padding: '0 8px',
    }),
    container: (provided) => ({
      ...provided,
      padding: '0px 0px',
      margin: '0px 0px',
    }),
  };

  return (
    <Select
      name={name}
      value={options.find((opt) => opt.value === value) || null}
      onChange={handleChange}
      options={options}
      isMulti={isMulti}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={disabled}
      isLoading={loading}
      onInputChange={endpoint ? handleInputChange : undefined}
      cacheOptions={cacheOptions}
      formatOptionLabel={formatOptionLabel}
      className="custom-select-container"
      classNamePrefix="custom-select"
      styles={customStyles}
      isSearchable={isSearchable}
    />
  );
};

CustomSelect.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.any, label: PropTypes.string })
  ),
  endpoint: PropTypes.string,
  isMulti: PropTypes.bool,
  placeholder: PropTypes.string,
  isClearable: PropTypes.bool,
  disabled: PropTypes.bool,
  loadOnMount: PropTypes.bool,
  fetchParams: PropTypes.object,
  cacheOptions: PropTypes.bool,
  formatOptionLabel: PropTypes.func,
};

export default CustomSelect;